import get from 'lodash/get';
import * as clientStorage from './client-storage';
import { StorageKey } from './client-storage';
import { AccessLevelType, HSYearsAccessType } from '../types/AccessLevel';
import User from '../types/User';
import { HealthCheck } from '../types/User';

export function logIn (email:string, password:string):Promise<User> {
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/auth/login`, {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      username: email,
      password,
    }),
  })
    .then(async (res) => {
      if (res.status === 429) {
        const error = await res.json();
        throw new Error(error?.message || 'Login attempt failed. Please check your email and password, and try again.');
      }
      if (res.ok) return res.json();

      throw new Error('Login attempt failed. Please check your email and password, and try again.')
    })
    .then(({ token, user }: { token:string, user:User }) => {
      clientStorage.clearAll();
      clientStorage.save(StorageKey.TOKEN, token);

      return user;
    }).catch(error => {
      throw new Error(error.message);
    } );
}

export function verifyToken (token:string):Promise<User>  {
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/auth/verify`, {
    method: 'GET',
    mode: 'cors',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  })
    .then(res => {
      if (res.ok) return res.json();

      throw new Error('The token is invalid or expired.')
    });
}

export function createPassword (token:string, password:string):Promise<User> {
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/auth/create-password`, {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      token,
      password
    }),
  })
    .then(res => {
      if (res.ok) return res.json();

      throw new Error('An error occurred, please try again.')
    })
    .then(({ token, user }: { token:string, user:User }) => {
      clientStorage.save(StorageKey.TOKEN, token);

      return user;
    });
}

export function resendCreatePasswordEmail (token:string):Promise<boolean> {
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/auth/resend-create-password`, {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  })
    .then(res => res.ok);
}

export function resetPassword (email:string):Promise<boolean> {
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/auth/reset-password`, {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email,
    }),
  })
    .then(res => res.ok);
}

export function isLoggedIn () {
  return !!clientStorage.get(StorageKey.TOKEN);
}

export function validatePassword (password:string):{ valid:boolean, error:string } {
  let valid = true;
  let error = '';

  if (password.length < 6) {
    valid = false;
    error = 'The password must be at least 6 characters long.';
  }

  if (!(/[A-Z]/.test(password))) {
    valid = false;
    error = 'The password must contain at least one capital character.';
  }

  if (!(/[0-9]/.test(password))) {
    valid = false;
    error = 'The password must contain at least one digit.';
  }

  return { valid, error };
}

export function getHighSchoolAccessYears (user?:User, rolloverDay?: number, rolloverMonth?: number):string[] {
  let highSchoolAccessYears = get(user, 'accessLevel.hsYearsAugust1', []);

  // if currentMonth is in between March and July then we need to go for hsYearsMarch1 otherwise hsYearsAugust1
  if (isDuringRollover(rolloverDay, rolloverMonth)) {
    highSchoolAccessYears = get(user, 'accessLevel.hsYearsMarch1', []);
  }

  return highSchoolAccessYears;
}

export function getMaxHighSchoolClassYear (user:User|undefined, currentRecruitingYear:number):number {
  if (!user) return currentRecruitingYear;

  const highSchoolAccessYears = getHighSchoolAccessYears(user);
  const isAdmin = user?.accessLevel?.type === AccessLevelType.ADMIN;

  const accessYearMapping:any = {
    'senior': currentRecruitingYear,
    'junior': currentRecruitingYear + Math.max(1, highSchoolAccessYears.length - 1),
    'sophomore': currentRecruitingYear + Math.max(2, highSchoolAccessYears.length - 1),
    'freshman': currentRecruitingYear + Math.max(3, highSchoolAccessYears.length - 1),
  };

  return (accessYearMapping[highSchoolAccessYears[0]] || currentRecruitingYear) + (isAdmin ? 1 : 0);
}

export function getUserAccessYears (user:User|undefined, currentRecruitingYear:number, rolloverDay?: number, rolloverMonth?: number):number[] {
  if (!user) return [currentRecruitingYear];

  const highSchoolAccessYears = getHighSchoolAccessYears(user, rolloverDay, rolloverMonth);
  let accessYears = [];
  for (let i = 0; i < highSchoolAccessYears.length; i++) {
    const accessYear = highSchoolAccessYears[i];
    if (accessYear === 'senior') {
      accessYears.push(currentRecruitingYear);
    } else if (accessYear === 'junior') {
      accessYears.push(currentRecruitingYear + 1);
    } else if (accessYear === 'sophomore') {
      accessYears.push(currentRecruitingYear + 2);
    } else if (accessYear === 'freshman') {
      accessYears.push(currentRecruitingYear + 3);
    }
  }
  return accessYears.sort((a, b) => a - b);
}

export function isDuringRollover (rolloverDay?: number, rolloverMonth?: number) {
  const currentMonth:number = new Date().getMonth() + 1;
  if (rolloverDay && rolloverMonth) {
    const march1 = new Date(new Date().getFullYear(), 2, 1);
    const rolloverDateCurrentYear = new Date(new Date().getFullYear(), rolloverMonth-1, rolloverDay);

    return march1 <= new Date() && new Date() < rolloverDateCurrentYear;
  }
  return currentMonth >= 3 && currentMonth <= 7;
}

export function healthCheck ():Promise<HealthCheck> {
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/auth/healthCheck`, {
    method: 'GET',
    cache: 'no-cache',
    headers: { 'Content-Type': 'application/json' },
  })
    .then(res => res.json());
}

// list of graduation class years list
export function getGradClassYearsList(
  rolloverDay?: number,
  rolloverMonth?: number,
  hsYearsAugust1: HSYearsAccessType[] = [],
  hsYearsMarch1: HSYearsAccessType[] = [],
  currentRecruitingYear?: number,
  isNfl: boolean = false,
): number[] | undefined {
  if (
    rolloverDay === undefined ||
    rolloverMonth === undefined ||
    currentRecruitingYear === undefined
  ) {
    console.warn("rolloverDay, rolloverMonth, or currentRecruitingYear is undefined");
    return;
  }

  const today = new Date();
  const march1 = new Date(today.getFullYear(), 2, 1);
  const rolloverDateCurrentYear = new Date(today.getFullYear(), rolloverMonth - 1, rolloverDay);

  const accessYears =
    march1 <= today && today < rolloverDateCurrentYear
      ? hsYearsMarch1
      : hsYearsAugust1;

  const gradYears: number[] = [];

  // Add year range based on isNfl
  const rangeStart = currentRecruitingYear - 10;
  const rangeEnd = isNfl ? currentRecruitingYear - 1 : currentRecruitingYear + 2;

  gradYears.push(...generateYearRange(rangeStart, rangeEnd));

  // for (const accessYear of accessYears) {
  //   let graduatingClass = currentRecruitingYear;
  //   if (accessYear === "junior") {
  //     graduatingClass += 1;
  //   } else if (accessYear === "sophomore") {
  //     graduatingClass += 2;
  //   } else if (accessYear === "freshman") {
  //     graduatingClass += 3;
  //   }
  //   gradYears.push(graduatingClass);
  // };
  
  // list of graduation Years
  return gradYears;
}

// Helper to generate range of years (inclusive)
export function generateYearRange(start: number, end: number): number[] {
  const range: number[] = [];
  for (let year = start; year <= end; year++) {
    range.push(year);
  }
  return range;
}
